import { defineStore } from "pinia";
import { handleApiResponse, handleError } from "@/utils/common.js";
import {
  PROFILE,
  GET_PROFILE,
  USER_STATISTICS,
  CONTACT_PREFERENCES,
  USER_ACCOUNT_STATUS,
  SUBSCRIPTION_CODE,
  SUBSCRIPTION_PLAN,
  PAYMENT_METHOD,
  SUBSCRIPTION,
  GET_SUBSCRIPTION,
} from "@/api/apiRoute";
import { authData, multipartDataWithToken } from "@/api/apiService.js";
import { errorMessage } from "@/utils/errorMessages.js";
import { useUserStore } from "./userStore";

export const useProfileStore = defineStore("profileStore", {
  state: () => ({
    userStore: useUserStore(),
    errorDisplayDuration: 4000,
    loading: false,
    errorMsg: "",
    userProfileData: null,
    isSubscriptionModalOpen: false,
    isPromoCodeModalOpen: false,
    isPromoCodeSuccessModalOpen: false,
    userContactPreferences: null,
    userStatistics: null,
    paymentMethodDetails: [],
    getSubscriptions: [],
    getUserSubscriptions: [],
    getSubscriptionUserList: [],
    planList: [],
    subscriptionPlanDetails: {
      subscription: {},
      subscriptionById: {},
    },
    isUpgradePlanModalOpen: false,
    isCancelSubscriptionModalOpen: false,
    isSuccessDialogOpen: false,
    selectedPlanId: null,
    isProcessingSubscription: false, // Prevent duplicate calls
    upgradePlanModalOpen: false,
    cancelSubscriptionSuccessModal: false,
    codePricePlan: [],
    codePriceSuccess: false,
    codePriceSuccessIndex: 0,
  }),
  getters: {
    currentSubscriptionPlan: (state) => state.subscriptionPlanDetails || {},
    selectedPlan: (state) => {
      const currentPlanId =
        state.currentSubscriptionPlan?.subscriptionById?.subscriptionPlanId;
      return state.planList.find((plan) => plan.id === currentPlanId) ?? null;
    },
  },
  actions: {
    clearErrorMessage() {
      this.errorMsg = "";
    },
    setErrorMessage(message) {
      this.errorMsg = errorMessage[message] || message;
    },
    showError(error) {
      this.setErrorMessage(error?.response?.data?.error);
      setTimeout(() => {
        this.clearErrorMessage();
      }, this.errorDisplayDuration);
      handleError(error);
      throw error;
    },
    setSuccessDialogOpen(isOpen) {
      this.isSuccessDialogOpen = isOpen;
    },
    async getUserStatistics() {
      this.loading = true;
      try {
        const response = await authData.get(USER_STATISTICS);
        this.userStatistics = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async getSubscriptionById(subscriptionId) {
      this.loading = true;
      try {
        const response = await authData.get(GET_SUBSCRIPTION(subscriptionId));
        this.subscriptionPlanDetails.subscriptionById = response.data.data;
        this.codePricePlan = response?.data?.data;
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async changeUserAccountStatus(status) {
      this.loading = true;
      try {
        const response = await authData.post(USER_ACCOUNT_STATUS, status);
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async getContactPreferences() {
      this.loading = true;
      try {
        const response = await authData.get(CONTACT_PREFERENCES);
        this.userContactPreferences = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async updateContactPreferences(payload) {
      this.loading = true;
      try {
        const response = await authData.patch(CONTACT_PREFERENCES, payload);
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async getSubscription() {
      this.loading = true;
      try {
        const response = await authData.get(SUBSCRIPTION);
        this.getSubscriptions = response?.data?.data || [];
        this.subscriptionPlanDetails.subscription =
          this.getSubscriptions?.[0] || {};
        const subscriptionId = this.getSubscriptions?.[0]?.id;
        if (subscriptionId) {
          await this.getSubscriptionsById(subscriptionId);
        }
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async getSubscriptionPricing() {
      this.loading = true;
      try {
        const response = await authData.get(SUBSCRIPTION_PLAN);
        this.planList = response?.data?.data || [];
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async checkSubscriptionCode(data) {
      this.loading = true;
      try {
        const response = await authData.post(SUBSCRIPTION_CODE, data);
        this.codePriceSuccess = true;
        this.codePricePlan = response?.data?.data;
        this.codePriceSuccessIndex = this.codePriceSuccessIndex + 1;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async getPaymentMethod() {
      this.loading = true;
      try {
        const response = await authData.get(PAYMENT_METHOD);
        this.paymentMethodDetails = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async savePaymentMethod() {
      this.loading = true;
      try {
        const response = await authData.post(PAYMENT_METHOD);
        await this.getPaymentMethod();
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // NEW: Poll subscription status until active
    async pollSubscriptionStatus(
      subscriptionId,
      maxAttempts = 15,
      delayMs = 2000
    ) {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const response = await authData.get(
            `${SUBSCRIPTION}/${subscriptionId}`
          );
          const subscription = response?.data?.data;

          // Check if subscription is active and has licenses
          if (
            subscription?.status === "active" &&
            subscription?.licenses?.length > 0
          ) {
            this.getUserSubscriptions = subscription;
            this.subscriptionPlanDetails.subscriptionById = subscription;
            return { success: true, data: subscription };
          }

          // Wait before next attempt
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        } catch (error) {
          // Ignore "no license" errors during polling
          if (
            error?.response?.data?.error !== "api_error_subscription_no_license"
          ) {
            throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      throw new Error(
        "Subscription activation timeout. Please refresh the page."
      );
    },

    async startSubscription(payload) {
      // Prevent duplicate calls
      if (this.isProcessingSubscription) {
        console.warn("Subscription already in progress");
        return;
      }

      this.loading = true;
      this.isProcessingSubscription = true;

      try {
        // Step 1: Create subscription
        const response = await authData.post(SUBSCRIPTION, payload);
        const subscriptionId = response?.data?.data?.id;

        if (!subscriptionId) {
          throw new Error("No subscription ID returned");
        }

        // Step 2: Poll until subscription is active
        await this.pollSubscriptionStatus(subscriptionId);

        // Step 3: Refresh all subscription data
        await this.getSubscription();

        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
        this.isProcessingSubscription = false;
      }
    },

    async getSubscriptionsById(id) {
      this.loading = true;
      try {
        const response = await authData.get(`${SUBSCRIPTION}/${id}`);
        this.getUserSubscriptions = response?.data?.data || [];
        this.subscriptionPlanDetails.subscriptionById =
          this.getUserSubscriptions || {};
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async getSubscriptionUsers(id) {
      this.loading = true;
      try {
        const response = await authData.get(`${SUBSCRIPTION}/${id}/users`);
        this.getSubscriptionUserList = response?.data?.data || [];
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async assignUserForSubscriptions(id, payload) {
      this.loading = true;
      try {
        const response = await authData.post(
          `${SUBSCRIPTION}/${id}/users`,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async cancelSubscription(id) {
      this.loading = true;
      try {
        const response = await authData.delete(`${SUBSCRIPTION}/${id}`);
        await this.getSubscription();
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async updateProfileImage(data) {
      this.loading = true;
      try {
        await multipartDataWithToken.post(PROFILE, data);
        await this.getUserStatistics();
        return null;
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async getUserProfile() {
      this.loading = true;
      try {
        const response = await authData.get(GET_PROFILE);
        this.userProfileData = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
  },
});
